import React from "react";
import { LinkOutlined } from "@ant-design/icons";
import { Button, Popover, Select, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";

import { traineesActions } from "@fitness-app/app-store";
import { type CalendarEvent } from "@fitness-app/data-models/entities/CalendarEvent";
import { type CalendlyEventPayload } from "@fitness-app/data-models/external/Calendly";

import { useUpdateCalendarEvent } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeCalendar/hooks/useUpdateCalendarEventStatus";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

interface ClientTableColumnProps {
  record: CalendarEvent & { trainee?: { firstName: string | null; lastName: string | null } | null };
  traineeId: string | null;
}

export const ClientTableColumn: React.FC<ClientTableColumnProps> = ({ traineeId, record }) => {
  const {
    mutation: { isLoading: isUpdatingEvent },
    linkClient,
  } = useUpdateCalendarEvent();
  const dispatch = useAppDispatch();
  const trainees = useAppSelector((state) => state.trainees.list);
  const [searchValue, setSearchValue] = React.useState("");
  const [popoverVisible, setPopoverVisible] = React.useState<string | null>(null);
  const [debouncedSearchValue, { isPending }] = useDebounce(searchValue, 300);
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();

  React.useEffect(() => {
    if (popoverVisible !== null) {
      dispatch(traineesActions.setSearchTerm(debouncedSearchValue));
      dispatch(traineesActions.fetchTrainees());
    }

    return () => {
      dispatch(traineesActions.setSearchTerm(""));
    };
  }, [debouncedSearchValue, popoverVisible, dispatch]);

  if (traineeId) {
    return (
      <Button type="link" className="px-0" onClick={() => navigate(`/trainee/details/${traineeId}`)}>
        {record.trainee?.firstName}{" "}
        {record.trainee?.lastName || (record.eventData as CalendlyEventPayload)?.name || t("unknownClient")}
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Typography.Text type="warning">
        {(record.eventData as CalendlyEventPayload)?.name || t("unknownClient")}
      </Typography.Text>
      <Popover
        trigger="click"
        open={popoverVisible === record.id}
        onOpenChange={(open) => setPopoverVisible(open ? record.id : null)}
        content={
          <div className="flex min-w-[250px] flex-col gap-2">
            <Typography.Text>
              Nazwa: {(record.eventData as CalendlyEventPayload)?.name || t("unknownClient")}
            </Typography.Text>
            <Typography.Text>
              Email: {(record.eventData as CalendlyEventPayload)?.email || t("unknownClient")}
            </Typography.Text>
            <Select
              showSearch
              filterOption={false}
              loading={isUpdatingEvent || isPending()}
              style={{ width: "100%" }}
              placeholder={t("trainees:search.placeholder")}
              value={undefined}
              onSearch={(value) => {
                setSearchValue(value);
              }}
              onSelect={(traineeId, option) => {
                if (typeof traineeId === "string" && traineeId) {
                  linkClient(record, option.metadata);
                  setPopoverVisible(null);
                  setSearchValue("");
                }
              }}
              options={trainees.map((trainee) => ({
                value: trainee.id,
                metadata: trainee,
                label: `${trainee.firstName} ${trainee.lastName} (${trainee.email})`,
              }))}
              notFoundContent="Brak wyników wyszukiwania"
            />
          </div>
        }
      >
        <Button className="shrink-0" type="dashed" size="small" icon={<LinkOutlined />} />
      </Popover>
    </div>
  );
};
